if (window.marketHandle && (window.marketHandle === 'canada' || window.marketHandle === 'europe') && !window.Shopify.designMode) {
    window.location = `${window.rootUrl}/collections/all-products/`;
}

// ------------------------------------------------------------------------

// GLOBALS

// ------------------------------------------------------------------------

const SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
//const THROUPLE_VARIANT_IDS = ["42877270491301","42877271736485","42877274783909"];
//const THROUPLE_VARIANT_ID = "44749321207973";

// ------------------------------------------------------------------------

// EVENT MANAGEMENT

// ------------------------------------------------------------------------

// CLICK EVENTS
const lpClickHandlers = new Map([
    ['button[data-checkout-button]', e => handleCheckout(e.target.closest('[data-checkout-button]'), e)],
    ['button[data-sticky-footer-button]', e => handleFooterCtaClick(e.target.closest('[data-sticky-footer-button]'), e)],
]);

document.addEventListener('click', (e) => {
    for (const [selector, handler] of lpClickHandlers) {
        if (e.target.closest(selector)) {
            handler(e);
            break;
        }
    }
});

// CHANGE EVENTS
const lpChangeHandlers = new Map([
    ['[data-product-select]', el => handleProductSelect(el)],
    ['[data-throuple-select]', el => handleGroupSelect(el)],
    ['[data-purchase-type-select]', el => handlePurchaseTypeSelect(el)],
    ['[data-selling-plan-select]', el => handleSellingPlanSelect(el)],
]);

document.addEventListener('change', (e) => {
    for (const [selector, handler] of lpChangeHandlers) {
        if (e.target.matches(selector)) {
            handler(e.target); // pass element
            break;
        }
    }
});

// ESTABLISH WINDOW LEVEL CACHE ON DOM LOADED
document.addEventListener('DOMContentLoaded', initWindowCache);
document.addEventListener('scroll', handleStickyFooterToggle, { passive: true });
window.addEventListener('resize', handleStickyFooterToggle);
window.addEventListener('load', handleStickyFooterToggle);

// ------------------------------------------------------------------------

// FETCH FUNCTIONS

// ------------------------------------------------------------------------

// small helpers
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function clearCartWithVerify({
  maxTries = 3,         // how many times to attempt a clear cycle
  pollTries = 5,        // how many times to poll /cart.js after each clear
  pollDelayMs = 150     // delay between polls
} = {}) {
  for (let attempt = 1; attempt <= maxTries; attempt++) {
    const clearRes = await fetch(`${SHOP_ROOT}cart/clear.js`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    // brief pause before polling the cart state
    await sleep(120);

    // poll /cart.js to confirm it's empty
    for (let i = 0; i < pollTries; i++) {
      const cart = await fetch(`${SHOP_ROOT}cart.js?t=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      }).then(r => r.json()).catch(() => null);

      const count = cart?.item_count ?? cart?.items?.length ?? 0;
      if (count === 0) return true;

      await sleep(pollDelayMs);
    }

    // backoff between attempts
    await sleep(200 * attempt);
  }
  return false; // clear never verified
}

// Fallback: replace the cart contents atomically
async function setCartToPayloadItems(payload) {
  // normalize to the format set.js expects
  // Accepts either {items:[{id,quantity,properties}...]} or single item fields
  const items = Array.isArray(payload?.items)
    ? payload.items
    : (payload?.id ? [{ id: payload.id, quantity: payload.quantity || 1, properties: payload.properties }] : []);

  if (!items.length) return false;

  const res = await fetch(`${SHOP_ROOT}cart/set.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
    body: JSON.stringify({ items })
  });
  return res.ok;
}

async function executeCheckout(payload, discountCodes) {
  // discountCodes can be string or string[]
  const codesArr = Array.isArray(discountCodes) ? discountCodes.filter(Boolean) : (discountCodes ? [discountCodes] : []);
  const discountParam = codesArr.length ? `?discount=${codesArr.map(encodeURIComponent).join(',')}` : '';

  try {
    const cleared = await clearCartWithVerify();

    if (!cleared) {
      console.warn('[executeCheckout] clear verification failed; using cart/set fallback');
      const setOk = await setCartToPayloadItems(payload);
      if (!setOk) {
        console.error('[executeCheckout] cart/set.js failed; aborting');
        return;
      }
    } else {
      // proceed with normal add
      const addRes = await fetch(`${SHOP_ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify(payload)
      });

      if (!addRes.ok) {
        console.error('[executeCheckout] add.js failed', addRes.status, await addRes.text());
        return;
      }
    }

    window.location.href = `${SHOP_ROOT}checkout${discountParam}`;
  } catch (err) {
    console.error('[executeCheckout] error', err);
  }
}


// ------------------------------------------------------------------------

// CAPTURE FUNCTIONS (get data from the page)

// ------------------------------------------------------------------------

function captureSection(el) {
    return el.closest('[data-lp-hero-section]') || null;
}

function captureSelectedProducts(section) {
    return section.querySelectorAll('[data-product-select]:checked');
}

function capturePurchaseType(section) {
    const el = section.querySelector('[data-purchase-type-select]');
    if (el) return el.value;

    return section.dataset.purchaseType === 'one_time_only' ? 'one-time' : 'subscription';
}

function captureSelectedSellingPlan(section) {
    if (capturePurchaseType(section) !== 'subscription') return null;
    if (!window.sellingPlans) setWindowCache();
    const el = section.querySelector('[data-selling-plan-select]');
    return el ? (el.value || null) : null; // e.g., "4-week"
}

function captureGiftState(section) {
    return section.dataset.giftsActive === 'true';
}

function captureSelectableCount(section) {
    return section.querySelectorAll('[data-product-select]').length;
}

function captureMinimumQuantity(section) {
    const minimumQuantity = Number.parseInt(section.dataset.minimumQuantity, 10);
    return Number.isFinite(minimumQuantity) && minimumQuantity > 0 ? minimumQuantity : 1;
}

// ------------------------------------------------------------------------

// PARSE FUNCTIONS (Transform / extract data)

// ------------------------------------------------------------------------

function parseVariantIdsFromInputs(variantEls) {
    return Array.from(variantEls).map(el => String(el.value).trim()).filter(Boolean);
}

function parseCheckoutPayload(section, variantIds, selectedSellingPlanValue) {
    if (!window.sellingPlans) setWindowCache();

    const giftsActive = captureGiftState(section);
    const customProps = parseGiftCustomProps(section);

    const items = variantIds.map((vid) => {
        const item = { id: Number(vid), quantity: 1 };

        if (selectedSellingPlanValue) {
            const plan = getPlanIdForVariant(vid, selectedSellingPlanValue);
            if (plan) item.selling_plan = plan.id; 
        }

        if (customProps) {
            item.properties = customProps;
        }

        return item;
    });

    if (giftsActive) {
       const gifts = parseCheckoutGifts(section);
       items.push(...gifts);
    }

    return { items };
}

function parseGiftCustomProps(section) {
    const selectedProducts = captureSelectedProducts(section);
    const json = {};

    const giftPropEls = section.querySelectorAll('[data-gift-item][data-prop-key][data-prop-value]');
    giftPropEls.forEach(el => {
        const giftThreshold = el.dataset.requiredQuantity ? parseInt(el.dataset.requiredQuantity) : 999;
        if (giftThreshold > selectedProducts.length) return;

        const propKey = el.dataset.propKey;
        const propValue = el.dataset.propValue;
        if (propKey && propValue && !json[propKey]) {
            json[propKey] = propValue;
        }
    });

    return Object.keys(json).length > 0 ? json : null;
}

function parseCheckoutGifts(section) {
    const selectedProducts = captureSelectedProducts(section);
    const selectedCount = selectedProducts.length;
    
    const giftPayloads = [];
    const addableGiftEls = section.querySelectorAll('[data-gift-item][data-add-to-order="true"]');
    addableGiftEls.forEach(el => {
        if (selectedCount >= parseInt(el.dataset.requiredQuantity) && !giftPayloads.find(gift => gift.id === el.dataset.variantId)) {
            giftPayloads.push(
                { 
                    id: Number(el.dataset.variantId), 
                    quantity: 1,
                    properties: {
                        '_gwp_item_id': `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                        '_free_gift': 'true'
                    }
                }
            );
        };
    });
    return giftPayloads;
}

function getPlanIdForVariant(variantId, planValue) {
    if (!planValue || !window.sellingPlans) return null;

    const groups = window.sellingPlans[String(variantId)];
    if (!Array.isArray(groups) || !groups.length) return null;

    const wanted = String(planValue).toLowerCase();
    const matchesFreq = (name) => String(name).toLowerCase().includes('order frequency');

    for (const group of groups) {
        if (Array.isArray(group.selling_plans)) {
            for (const sp of group.selling_plans) {
                const opt = (sp.options || []).find(o => matchesFreq(o.name));
                if (opt && String(opt.value).toLowerCase() === wanted) {
                    return sp;
                }
            }
        }

        // Fallback: match group.options values, then take first plan
        const freqOpt = (group.options || []).find(o => matchesFreq(o.name));
        if (freqOpt && (freqOpt.values || []).some(v => String(v).toLowerCase() === wanted)) {
            const first = group.selling_plans && group.selling_plans[0];
            if (first && first.id != null) return first;
        }
    }

    return null;
}

function parseSectionDiscount(section) {
    if (section.dataset.discountCode && section.dataset.discountPercentage) {
        const rate = parseInt(section.dataset.discountPercentage);
        return (100 - rate) * 0.01;
    }
    return 1
}

function parseSectionDiscountCode(section) {
    if (section.dataset.discountCode) {
        return section.dataset.discountCode;
    }
    return null;
}

function parseSelectedVariantQuantity(section, variantId) {
    // ONCE QUANTITY SELECTION IS ADDED, WE'LL NEED TO DO A LIVE PARSE. FOR NOW, WE'LL USE A DEFAULT.
    return 1;
}

function parseDefaultVariantPrice(variant) {
    const originalPrice = variant.price;
    const comparePrice = variant.compare_at_price && variant.compare_at_price > variant.price 
        ? variant.compare_at_price
        : variant.price;

    return { 'price': originalPrice, 'compare_price': comparePrice };
}

function parseSellingPlanDiscount(variant, sellingPlan) {
    if (!sellingPlan) return 0;

    const plan = getPlanIdForVariant(variant.id, sellingPlan);
    if (!plan) return 0;

    const planAdjustments = plan.price_adjustments?.length > 0 ? plan.price_adjustments[0] : null;
    if (!planAdjustments) return 0;

    if (planAdjustments.value_type === 'percentage') {
        return (variant.price * (planAdjustments.value / 100));
    } else {
        return planAdjustments.value;
    }
}

function formatShopifyMoney(cents, opts = {}) {
    const cfg = Object.assign({}, window.pageCurrency || {}, opts);
    const divisor = 10 ** (Number(cfg.minorUnit) || 2);
    const amount = cents / divisor;
  
    return new Intl.NumberFormat(cfg.locale, {
        style: 'currency',
        currency: cfg.currency,
        minimumFractionDigits: cfg.minorUnit,
        maximumFractionDigits: cfg.minorUnit
    }).format(amount);
}
  

function parseVariantLinePrice(section, variantId, sellingPlan) {
    const variant = parseVariantJson(String(variantId));
    if (!variant) return { 'price': 0,'compare_price': 0 };

    const baseVariantPricing = parseDefaultVariantPrice(variant);
    
    const sectionDiscount = parseSectionDiscount(section) || 1;
    const selectedVariantQuantity = parseSelectedVariantQuantity(section, variantId) || 1;
    const sellingPlanDiscount = parseSellingPlanDiscount(variant, sellingPlan) || 0;

    return {
        'price': (baseVariantPricing.price - sellingPlanDiscount) * sectionDiscount * selectedVariantQuantity,
        'compare_price': baseVariantPricing.compare_price * selectedVariantQuantity
    }

}

function parseVariantJson(variantId) {
    const variant = window.variants[String(variantId)];
    return variant ? variant : null;
}

function parseGiftPricing(section, itemPricing) {
    const giftsActive = captureGiftState(section);
    if (!giftsActive) return itemPricing;

    const giftsWithValue = section.querySelectorAll('[data-gift-item][data-value].gift--active');
    if (giftsWithValue.length === 0) return itemPricing;

    giftsWithValue.forEach(gift => {
        itemPricing.compare_price += parseFloat(gift.dataset.value);
    });
    
    return itemPricing;
}

function parseSelectionPricing(section, variantIds, selectedSellingPlan) {
    const pricing = { 'price': 0, 'compare_price': 0 };
    variantIds.forEach(variantId => {
        const linePrice = parseVariantLinePrice(section, variantId, selectedSellingPlan);
        pricing.price += linePrice.price;
        pricing.compare_price += linePrice.compare_price;
    });
    return pricing;
}

// ------------------------------------------------------------------------

// HANDLERS

// ------------------------------------------------------------------------

// Product Selection
function handleProductSelect(target) {
    const section = captureSection(target);
    const selectedProducts = captureSelectedProducts(section);

    executeProductSelection(section, selectedProducts);
}

function handlePurchaseTypeSelect(target) {
    const section = captureSection(target);
    const isSubscription = target.value === 'subscription';

    section.querySelectorAll('[data-selling-plan-options]').forEach(wrapper => {
        wrapper.hidden = !isSubscription;
    });

    handleContentUpdates(section, captureSelectedProducts(section));
}

function handleSellingPlanSelect(target) {
    const section = captureSection(target);
    handleContentUpdates(section, captureSelectedProducts(section));
}

function executeProductSelection(section, selectedProducts) {
    handleContentUpdates(section, selectedProducts);
    toggleCheckoutButtonStates(section, selectedProducts.length);
}

function handleContentUpdates(section, selectedProducts) {
    const variantIds = parseVariantIdsFromInputs(selectedProducts);
    handleGiftDisplayUpdates(section, variantIds.length);
    // DISABLED THROUPLE SUPPORT FOR CLEANER MESSAGING
    //const reparsedForThrouple = reparseSelectionWithThrouple(selectedVariantIds);
    //const variantIds = reparsedForThrouple.variant_ids;
    handleGroupSelectToggle(section, captureSelectableCount(section) === variantIds.length);

    const selectedSellingPlan = captureSelectedSellingPlan(section);
    const itemPricing = parseSelectionPricing(section, variantIds, selectedSellingPlan);
    const pricing = parseGiftPricing(section, itemPricing);

    handleUpdatePricing(section, pricing); 
}

function handleUpdatePricing(section, pricing) {
    const priceDisplays = section.querySelectorAll('[data-price-display]');
    priceDisplays.forEach(display => {
        display.textContent = formatShopifyMoney(pricing.price);
    });
    const comparePriceDisplays = section.querySelectorAll('[data-compare-price-display]');
    comparePriceDisplays.forEach(display => {
        display.hidden = pricing.compare_price <= pricing.price;
        display.textContent = formatShopifyMoney(pricing.compare_price);
    });
}

function toggleCheckoutButtonStates(section, selectedCount) {
    const minimumQuantity = captureMinimumQuantity(section);
    const checkoutButtons = section.querySelectorAll('[data-checkout-button]');
    checkoutButtons.forEach(button => {
        button.disabled = selectedCount < minimumQuantity || section.dataset.processing === 'true';
    });
}

// Sticky Footer
function handleFooterCtaClick(target, event) {
    const section = captureSection(target);
    const selectedProducts = captureSelectedProducts(section);
    const minimumQuantity = captureMinimumQuantity(section);

    if (selectedProducts.length < minimumQuantity) {
        section.querySelector('[data-product-select]').closest('div').scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
        handleCheckout(section.querySelector('[data-checkout-button]'), null);
    }
}

// Group Selection
function handleGroupSelect(target) {
    const section = captureSection(target);
    const selectionInputs = section.querySelectorAll('[data-product-select]');
    selectionInputs.forEach(input => {
        input.checked = target.checked;
    });
    executeProductSelection(section, captureSelectedProducts(section));
}

function handleGroupSelectToggle(section, state) {
    section.querySelectorAll('[data-throuple-select]').forEach(input => {
        input.checked = state;
    });
}

// Gift selection
function handleGiftDisplayUpdates(section, itemCount) {
    const giftsActive = captureGiftState(section);
    if (!giftsActive) return;

    const giftDisplays = section.querySelectorAll('[data-content="gift-display"]');
    giftDisplays.forEach(display => {
        updateGiftDisplayContent(display, itemCount);
    });
}

function updateGiftDisplayContent(element, itemCount) {
    const gifts = element.querySelectorAll('[data-gift-item]');
    let activeCount = 0;
    gifts.forEach(gift => {
        const threshold = gift.dataset.requiredQuantity ? parseInt(gift.dataset.requiredQuantity) : 1;
        const active = itemCount >= threshold;
        gift.classList.toggle('gift--active', active);
        if (active) activeCount++;
    });
    const unlockedDisplay = element.querySelector('[data-unlocked-count]');
    if (unlockedDisplay) unlockedDisplay.querySelector('[data-content="unlocked-gift-count"]').textContent = activeCount;

    const unlockedWrapper = element.querySelector('[data-unlocked-count]');
    if (unlockedWrapper) unlockedWrapper.setAttribute('data-unlocked-count', activeCount);
}

// Checkout Click
async function handleCheckout(target, event) {
    const section = captureSection(target);
    if (!section || section.dataset.processing === 'true') return;

    const variantIds = parseVariantIdsFromInputs(captureSelectedProducts(section));
    if (variantIds.length < captureMinimumQuantity(section)) return;

    // DISABLED THROUPLE SUPPORT FOR CLEANER MESSAGING
    //const reparsedForThrouple = reparseSelectionWithThrouple(selectedVariantIds);
    //const variantIds = reparsedForThrouple.variant_ids;    

    section.dataset.processing = 'true';
    toggleCheckoutButtonStates(section, variantIds.length);

    const selectedSellingPlan = captureSelectedSellingPlan(section);
    const payload = parseCheckoutPayload(section, variantIds, selectedSellingPlan);

    const discountCode = parseSectionDiscountCode(section);

    try {
        await executeCheckout(payload, discountCode);
    } finally {
        section.dataset.processing = 'false';
        toggleCheckoutButtonStates(section, variantIds.length);
    }
}

// Sticky Footer 
function handleStickyFooterToggle() {
    const footer = document.querySelector('[data-cta-footer]');
    if (!footer) return;

    const toggleParent = document.querySelector('[data-inline-cta]');
    if (!toggleParent) return;

    const globalFooter = document.querySelector('footer');

    const rect = toggleParent.getBoundingClientRect();
    const footerRect = globalFooter ? globalFooter.getBoundingClientRect() : { top: 0, bottom: 0 };
    const scrolledPast = rect.bottom < 0;
    const footerVisible = footerRect.top < window.innerHeight && footerRect.bottom > 0;

    footer.classList.toggle('active', scrolledPast && !footerVisible);
    
}

// ------------------------------------------------------------------------

// GLOBAL LOOKUPS

// ------------------------------------------------------------------------

function initWindowCache() {
    const cacheLookups = [
        {
            "selector": "[data-variant-json]",
            "windowKey": "variants",
            "dataSetKey": "variantJson"
        },
        {
            "selector": "[data-selling-plan-for]",
            "windowKey": "sellingPlans",
            "dataSetKey": "sellingPlanFor"
        }
    ]

    cacheLookups.forEach(lookup => {
        cacheDroppedData(lookup.selector, lookup.windowKey, lookup.dataSetKey);
    });
}

function cacheDroppedData(selector, windowKey, datasetKey) {
    const drops = document.querySelectorAll(selector);
    const cache = {};

    drops.forEach(drop => {
        const key = drop.dataset[datasetKey];
        if (key && !cache[key]) {
            cache[key] = JSON.parse(drop.textContent || '{}');
        }
    });

    window[windowKey] = cache;
}

// PAGE SHOW EVENT TO HANDLE BACK BUTTON NAVIGATION
window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
        reInitOnBackload();
    }
});

function reInitOnBackload() {
    const offerSections = document.querySelectorAll('[data-lp-hero-section]');
    offerSections.forEach(section => {
        if (section.querySelector('[data-product-select]:checked')) {
            handleProductSelect(section.querySelector('[data-product-select]:checked'));
        }
    });
}
